import React, { useState, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { FaCalendarPlus } from "react-icons/fa";
import Step1MeetingDetails from "./Step1MeetingDetails";
import Step2Template from "./Step2Template";
import Step3Review from "./Step3Review";
import { CustomButton } from "../../components/CustomButton";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import { useSelector } from "react-redux";
import { createMeetingInfo } from "../../reducers/meetingSlice";
import { useDispatch } from "react-redux";
import { Toast } from "primereact/toast";

const NewMeetingForm = ({ onSchedule }) => {
  const step2ValidateRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const methods = useForm();
  const toast = useRef(null);

  const errorToast = (message) => {
    toast.current.show({
      severity: "error",
      detail: message,
    });
  };

  const successToast = (message) => {
    toast.current.show({
      severity: "success",
      detail: message,
    });
  };
  const [step, setStep] = useState(0);
  const [meetingInfo, setMeetingInfo] = useState({});
  const [templateInfo, setTemplateInfo] = useState({});
  const { user } = useSelector((state) => state.authenticate);
  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));
  const reset = () => {
    setMeetingInfo({});
    setTemplateInfo({});
    setStep(0);
  };

  const itemslist = [
    { label: "Meetings", url: "/meeting" },
    { label: "New Meeting", url: "/meeting/new" },
  ];

  const steps = ["Details", "Template", "Review"];

  const handleSchedule = () => {
    if (meetingInfo && templateInfo) {
      const newMeeting = {
        meetingName: meetingInfo.title,
        organizerName: meetingInfo.organizer,
        customerIds: meetingInfo.customerIds || [],
        groupIds: meetingInfo.groupIds || [],
        date: meetingInfo.date,
        time: meetingInfo.time,
        reminderTime: meetingInfo.reminder, // e.g., "10m"
        url: meetingInfo.url,
        companyId: user.companyId,
        bookingTemplateId: templateInfo?.booking?.id || null,
        reminderTemplateId: templateInfo.reminder?.id || null,
        confirmationTemplateId: templateInfo?.confirmation?.id || null,
        previewUrl: "",
        status: "upcoming",
      };

      dispatch(createMeetingInfo(newMeeting))
        .unwrap()
        .then((res) => {
          successToast("Meeting scheduled successfully");
          setTimeout(() => {
            navigate("/meeting");
          }, 1000);
        })
        .catch((err) => {
          console.error("Failed to create meeting ❌", err);
          if (err.response && err.response.data && err.response.data.error) {
            errorToast(err.response.data.error);
          } else {
            errorToast("Failed to schedule meeting. Please try again.");
          }
        });
    }
  };

  const renderBody = () => {
    switch (step) {
      case 0:
        return (
          <Step1MeetingDetails
            defaultValues={meetingInfo}
            onNext={(data) => {
              setMeetingInfo(data);
              next();
            }}
          />
        );
      case 1:
        return (
          <Step2Template
            meetingInfo={meetingInfo}
            onNext={(tpls) => {
              setTemplateInfo(tpls);
              next();
            }}
            validateRef={step2ValidateRef}
          />
        );
      case 2:
        return (
          <Step3Review
            meetingInfo={meetingInfo}
            templateInfo={templateInfo}
            coverImage={templateInfo.coverImage}
            onBack={back}
            onSchedule={handleSchedule}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Toast ref={toast} />
      <div
        style={{
          padding: "2rem",
          fontFamily: "Inter, sans-serif",
          background: theme.base_background,
          color: theme.text_primary,
          minHeight: "100vh",
        }}
      >
        <CustomBreadcrumb
          home={{ label: "Meeting Scheduler", url: "/meeting" }}
          itemslist={itemslist}
          style={{ borderRadius: "8px" }}
        />

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: ".5rem",
            marginBottom: "1.5rem",
            marginTop: "1.5rem",
          }}
        >
          <FaCalendarPlus size={24} color={theme.primary} />
          <h2 style={{ margin: 0 }}>Schedule New Meeting</h2>
        </div>

        {/* Stepper */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            background: "#F9FAFB",
            padding: "0.75rem 1.5rem",
            borderRadius: "12px",
            border: `1px solid ${theme.border || "#E0E0E0"}`,
            marginBottom: "1.5rem",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "1.5rem" }}>
            {steps.map((n, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".5rem",
                  cursor: i <= step ? "pointer" : "default",
                  opacity: i <= step ? 1 : 0.5,
                }}
                onClick={() => i <= step && setStep(i)}
              >
                <Badge
                  value={i + 1}
                  style={{
                    background: i <= step ? theme.primary : "#D1D5DB",
                    color: theme.sideBarText,
                  }}
                />
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "0.95rem",
                    borderBottom:
                      i === step ? `2px solid ${theme.primary}` : "none",
                  }}
                >
                  {n}
                </span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span
              style={{
                color: theme.text_secondary,
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
              onClick={() => navigate("/meeting")}
            >
              Cancel
            </span>

            <Button
              label="Reset"
              outlined
              style={{
                borderColor: theme.primary,
                color: theme.primary,
                backgroundColor: "transparent",
              }}
              onClick={reset}
            />

            <CustomButton
              label={step === 2 ? "Schedule meeting" : "Save & Continue"}
              style={{
                backgroundColor: theme.primary,
                border: "none",
                color: theme.sideBarText,
              }}
              onClick={
                step === 0
                  ? () => {
                      // 🔁 Triggers Step1MeetingDetails form submit
                      document.querySelector("form").dispatchEvent(
                        new Event("submit", {
                          cancelable: true,
                          bubbles: true,
                        })
                      );
                    }
                  : step === 1
                  ? () => {
                      if (step2ValidateRef.current) {
                        step2ValidateRef.current();
                      }
                    }
                  : handleSchedule
              }
            />
          </div>
        </div>

        {/* Form Body */}
        <div
          style={{
            background: theme.sideBarText,
            borderRadius: 8,
            padding: "2rem",
            boxShadow: "0 2px 6px rgba(0,0,0,.1)",
          }}
        >
          {renderBody()}
        </div>
      </div>
    </FormProvider>
  );
};

export default NewMeetingForm;
